import { Button } from "@mui/material"
import React from "react"
import { useTranslation } from "react-i18next"
import InputField from "src/components/atom/InputField"
import InputNameField from "src/components/atom/InputNameField"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { PROVINCES } from "src/modules/const"
import { Box } from "src/UILibrary"

type Props = {
  companyName: string
  handleCompanyName: any
  companyNameJP: string
  handleCompanyNameJP: any
  firstName: string
  lastName: string
  firstNameJP: string
  lastNameJP: string
  handleFirstName: any
  handleLastName: any
  handleFirstNameJP: any
  handleLastNameJP: any
  areaID: number
  handleAreaID: any
  phoneNumber: string
  handlePhoneNumber: any
  headPostCode: string
  headProvince: string
  headCity: string
  headStreeet: string
  headBuilding: string
  headSiteURL: string
  headPhoneNumber: string
  handleHeadPostCode: any
  handleHeadProvince: any
  handleHeadCity: any
  handleHeadStreet: any
  handleHeadBuilding: any
  handleHeadSiteURL: any
  handleHeadPhoneNumber: any
  officePostCode: string
  officeProvince: string
  officeCity: string
  officeStreeet: string
  officeBuilding: string
  officeSiteURL: string
  officePhoneNumber: string
  handleOfficePostCode: any
  handleOfficeProvince: any
  handleOfficeCity: any
  handleOfficeStreet: any
  handleOfficeBuilding: any
  handleOfficeSiteURL: any
  handleOfficePhoneNumber: any
  companyNameValidationMessage: string
  companyNameJPValidationMessage: string
  nameValidationMessage: string
  phoneNumberValidationMessage: string
  headPostCodeValidationMessage: string
  headCityValidationMessage: string
  headSiteURLValidationMessage: string
  headPhoneNumberValidationMessage: string
  officePostCodeValidationMessage: string
  officeSiteURLValidationMessage: string
  officePhoneNumberValidationMessage: string
  handleHeadPostCodeInput: any
  handleOfficePostCodeInput: any
  handleNext: () => void
}

export const CompanyGeneralInformation = ({
  companyName,
  handleCompanyName,
  companyNameJP,
  handleCompanyNameJP,
  firstName,
  lastName,
  firstNameJP,
  lastNameJP,
  handleFirstName,
  handleLastName,
  handleFirstNameJP,
  handleLastNameJP,
  areaID,
  handleAreaID,
  phoneNumber,
  handlePhoneNumber,
  headPostCode,
  headProvince,
  headCity,
  headStreeet,
  headBuilding,
  headSiteURL,
  headPhoneNumber,
  handleHeadPostCode,
  handleHeadProvince,
  handleHeadCity,
  handleHeadStreet,
  handleHeadBuilding,
  handleHeadSiteURL,
  handleHeadPhoneNumber,
  officePostCode,
  officeProvince,
  officeCity,
  officeStreeet,
  officeBuilding,
  officeSiteURL,
  officePhoneNumber,
  handleOfficePostCode,
  handleOfficeProvince,
  handleOfficeCity,
  handleOfficeStreet,
  handleOfficeBuilding,
  handleOfficeSiteURL,
  handleOfficePhoneNumber,
  companyNameValidationMessage,
  companyNameJPValidationMessage,
  nameValidationMessage,
  phoneNumberValidationMessage,
  headPostCodeValidationMessage,
  headCityValidationMessage,
  headSiteURLValidationMessage,
  headPhoneNumberValidationMessage,
  officePostCodeValidationMessage,
  officeSiteURLValidationMessage,
  officePhoneNumberValidationMessage,
  handleHeadPostCodeInput,
  handleOfficePostCodeInput,
  handleNext,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const { areas } = useLoginContext()

  return (
    <Box maxWidth="md">
      <InputTextField
        name={t("company_name")}
        required
        sx={{ width: "100%" }}
        text={companyName}
        handleText={handleCompanyName}
        validationMessage={companyNameValidationMessage}
        maxLength={100}
      />
      <InputTextField
        name={t("company_name_jp")}
        required
        sx={{ width: "100%", mt: "10px" }}
        text={companyNameJP}
        handleText={handleCompanyNameJP}
        validationMessage={companyNameJPValidationMessage}
        maxLength={100}
      />
      <InputNameField
        name={t("charge_name")}
        required
        sx={{ mt: "30px" }}
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={handleFirstName}
        handleLastName={handleLastName}
        handleFirstNameJP={handleFirstNameJP}
        handleLastNameJP={handleLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "30px", mb: "40px", width: "200px" }}
        selected={areaID}
        handleSelected={handleAreaID}
        datas={areas}
      ></SelectField>
      <InputTextField
        name={t("phone_number")}
        required
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={phoneNumber}
        handleText={handlePhoneNumber}
        validationMessage={phoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputField name={t("company_address")} required sx={{ mt: "80px" }} />
      <InputTextField
        name={t("post_code")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={headPostCode}
        handleText={handleHeadPostCode}
        validationMessage={headPostCodeValidationMessage}
        handleFocusOut={handleHeadPostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        sx={{ mt: "30px", width: "200px" }}
        selected={headProvince}
        handleSelected={handleHeadProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        sx={{ mt: "30px" }}
        text={headCity}
        handleText={handleHeadCity}
        validationMessage={headCityValidationMessage}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={headStreeet}
        handleText={handleHeadStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={headBuilding}
        handleText={handleHeadBuilding}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("site_url")}
        sx={{ mt: "30px" }}
        text={headSiteURL}
        handleText={handleHeadSiteURL}
        validationMessage={headSiteURLValidationMessage}
        maxLength={200}
      ></InputTextField>
      <InputTextField
        name={t("phone_number")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={headPhoneNumber}
        handleText={handleHeadPhoneNumber}
        validationMessage={headPhoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputField name={t("service_input")} sx={{ mt: "80px" }} />
      <InputTextField
        name={t("post_code")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={officePostCode}
        handleText={handleOfficePostCode}
        validationMessage={officePostCodeValidationMessage}
        handleFocusOut={handleOfficePostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        sx={{ mt: "30px", width: "200px" }}
        selected={officeProvince}
        handleSelected={handleOfficeProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        sx={{ mt: "30px" }}
        text={officeCity}
        handleText={handleOfficeCity}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={officeStreeet}
        handleText={handleOfficeStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={officeBuilding}
        handleText={handleOfficeBuilding}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("site_url")}
        sx={{ mt: "30px" }}
        text={officeSiteURL}
        handleText={handleOfficeSiteURL}
        validationMessage={officeSiteURLValidationMessage}
        maxLength={200}
      ></InputTextField>
      <InputTextField
        name={t("phone_number")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={officePhoneNumber}
        handleText={handleOfficePhoneNumber}
        validationMessage={officePhoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <Button
        variant="contained"
        sx={{
          minWidth: "240px",
          py: "16px",
          px: "88px",
          fontSize: "1rem",
          fontFamily: "Noto Sans JP !important",
          lineHeight: "1rem",
          fontWeight: 700,
          my: "80px",
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          borderRadius: 0,
          ":hover": {
            bgcolor: "#2DA389",
          },
        }}
        onClick={() => handleNext()}
      >
        {t("next")}
      </Button>
    </Box>
  )
}
